import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import InvestorLpImage from "../../../../../../assets/images/landing-page/invesotor-lp-image.png";
import { openExternalLink } from '../../../../../../utils/navigation';
import AndroidDownloadIcon from "../../../../../../assets/images/android_download.png";
import IOSDownloadIcon from "../../../../../../assets/images/ios_download.png";
import TheVybeIcon from "../../../../../../assets/images/landing-page/the-vybe-icon.webp";
import HavenGardensIcon from "../../../../../../assets/images/landing-page/haven-gardens-icon.webp";
import TheVyneIcon from "../../../../../../assets/images/landing-page/the-vyne-icon.webp";
import { useContentArea } from '../../../../../../context/ContentAreaContext';
import { colorPalette } from '../../../../../../utils/theme';
import { CenterImage, Section, Stage, LeftCard, LeftFloatingBadge, ChartNumber, ChartLabel, RightCard, RightFloatingBadge, ProgressSection, ProgressTitle, ProgressItem, ProgressLeft, Avatar, ProjectName, ProgressBarContainer, ProgressBar, ProgressPercent, BottomFloatingBadge, StageFooter, FooterBadge, Headline, Subtitle, ButtonContainer, UnitNumber, CenterImageMobile } from './control-anywhere.styles';
import { useTranslation } from 'react-i18next';
import HeaderMobileEN from "../../../../../../assets/images/landing-page/header_investors_EN.webp";
import HeaderMobileDE from "../../../../../../assets/images/landing-page/header_investors_DE.webp";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip, Legend);

const ControlAnywhere: React.FC = () => {
  const { contentAreaRef } = useContentArea();
  const { t, i18n } = useTranslation();

  // Refs
  const sectionRef = useRef<HTMLElement>(null);
  const chartRef = useRef<any>(null);

  // States
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string | CanvasGradient>(colorPalette.blue400);
  const [lineBorderColor, setLineBorderColor] = useState<string | CanvasGradient>("");

  // Sample chart data for demonstration
  const chartData = {
    years: [2020, 2021, 2022, 2023, 2024],
    volumeData: [1500000, 2100000, 2800000, 3200000, 3731763]
  };

  // Progress bar data with target values
  const progressData = [
    { icon: TheVybeIcon, name: 'The Vybe', units: '801, 1706', targetProgress: 90 },
    { icon: HavenGardensIcon, name: 'Haven Gardens', units: '1611', targetProgress: 57 },
    { icon: TheVyneIcon, name: 'The Vyne', units: '713', targetProgress: 24 }
  ];

  // Calculate animated progress based on scroll
  const getAnimatedProgress = (targetProgress: number) => {
    if (!isVisible) return 0;
    return Math.round(scrollY * targetProgress);
  };

  // Chart gradient setup
  useEffect(() => {
    if (chartRef && chartRef.current) {
      const chart = chartRef.current;
      const canvas = chart.canvas.getContext('2d');

      const { chartArea } = chart;
      if (!chartArea) return;

      const { top, bottom } = chartArea;

      // Create gradient for the line border
      const lineGradient = canvas.createLinearGradient(0, 0, canvas.canvas.width, 0);
      lineGradient.addColorStop(0, '#0F1C4D');
      lineGradient.addColorStop(0.68, '#276EF1');
      setLineBorderColor(lineGradient);

      // Create gradient for the background fill
      const fillGradient = canvas.createLinearGradient(0, top, 0, bottom);
      fillGradient.addColorStop(0, 'rgba(39, 110, 241, 0.25)');
      fillGradient.addColorStop(0.95, 'rgba(39, 110, 241, 0)');
      setBackgroundColor(fillGradient);
    }
  }, [chartRef]);

  // Parallax scroll effect
  useEffect(() => {
    let ticking = false;

    const updateScrollY = () => {
      if (!sectionRef.current || !contentAreaRef.current) return;

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const containerRect = contentAreaRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;

      // Calculate relative position within the scrollable container
      const relativeTop = sectionRect.top - containerRect.top;
      const relativeBottom = sectionRect.bottom - containerRect.top;

      // Check if section is in viewport of the container
      const isInView = relativeTop < containerHeight && relativeBottom > 0;
      setIsVisible(isInView);

      if (isInView) {
        // Calculate scroll progress based on container viewport
        // When section top is at bottom of container: progress = 0
        // When section top is at top of container: progress = 1
        const scrollProgress = Math.max(0, Math.min(1, (containerHeight - relativeTop) / containerHeight));
        setScrollY(scrollProgress);
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollY);
        ticking = true;
      }
    };

    const scrollContainer = contentAreaRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      updateScrollY(); // Initial call

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [contentAreaRef]);

  // Calculate parallax transforms based on scroll progress
  const getParallaxTransform = (speed: number, direction: 'x' | 'y' = 'y', baseTransform?: string, disableOnMobile: boolean = false) => {
    if (!isVisible) return baseTransform || '';

    // Check if we should disable parallax on mobile
    const isMobile = window.innerWidth <= 767.98;
    if (disableOnMobile && isMobile) return baseTransform || '';

    const movement = scrollY * speed;
    const parallaxTransform = direction === 'x'
      ? `translate3d(${movement}px, 0, 0)`
      : `translate3d(0, ${movement}px, 0)`;

    return baseTransform ? `${baseTransform} ${parallaxTransform}` : parallaxTransform;
  };

  // Chart configuration
  const data = {
    labels: chartData.years,
    datasets: [
      {
        label: 'Portfolio Value',
        data: chartData.volumeData,
        fill: true,
        backgroundColor: backgroundColor,
        borderColor: lineBorderColor,
        borderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 0,
        pointHoverRadius: 0,
        pointBorderColor: "transparent",
        borderCapStyle: 'round' as CanvasLineCap,
        borderJoinStyle: 'round' as CanvasLineJoin,
        tension: 0.4,
      },
    ],
  };

  const options = useMemo(() => {
    return {
      type: "line",
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      scales: {
        x: {
          display: false,
        },
        y: {
          display: false,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };
  }, []);

  return (
    <Section ref={sectionRef}>
      <Stage>
        <CenterImage
          src={InvestorLpImage}
          alt="App Preview"
          style={{ transform: getParallaxTransform(-15, 'y', 'translateX(-50%)') }}
        />

        {/* Mobile Image */}
        <CenterImageMobile
          src={i18n.language === 'en' ? HeaderMobileEN : HeaderMobileDE}
          alt="Mobile Header"
        />

        <LeftCard style={{ transform: getParallaxTransform(-25, 'x', undefined, true) }}>
          <LeftFloatingBadge style={{ transform: getParallaxTransform(-50, 'y', undefined, true) }}>
            {t('pages.home.control_section.rent_paid_badge')}
          </LeftFloatingBadge>
          <ChartNumber>{t('pages.home.control_section.portfolio_value')}</ChartNumber>
          <ChartLabel>{t('pages.home.control_section.portfolio_volume_purchase_price')}</ChartLabel>
          <div style={{ height: '80px', position: 'relative' }}>
            <Line ref={chartRef} data={data} options={options} />
          </div>
        </LeftCard>

        <RightCard style={{ transform: getParallaxTransform(30, 'x', undefined, true) }}>
          <RightFloatingBadge style={{ transform: getParallaxTransform(40, 'x', undefined, true) }}>
            {t('pages.home.control_section.documents_updated_badge')}
          </RightFloatingBadge>
          <ProgressSection>
            <ProgressTitle>{t('pages.home.control_section.construction_progress')}</ProgressTitle>
            {progressData.map((project, index) => {
              const animatedProgress = getAnimatedProgress(project.targetProgress);
              return (
                <ProgressItem key={project.name}>
                  <ProgressLeft>
                    <Avatar src={project.icon} alt={project.name} />
                    <div>
                      <ProjectName>{project.name}</ProjectName>
                      <UnitNumber>{project.units}</UnitNumber>
                    </div>
                  </ProgressLeft>
                  <ProgressBarContainer>
                    <ProgressBar progress={animatedProgress} />
                  </ProgressBarContainer>
                  <ProgressPercent>{animatedProgress}%</ProgressPercent>
                </ProgressItem>
              );
            })}
          </ProgressSection>
        </RightCard>

        <BottomFloatingBadge style={{ transform: getParallaxTransform(60, 'y', undefined, true) }}>
          {t('pages.home.control_section.contract_extended_badge')}
        </BottomFloatingBadge>

        <StageFooter>
          <FooterBadge>{t('pages.home.control_section.app_badge')}</FooterBadge>
          <Headline>{t('pages.home.control_section.headline')}</Headline>
          <Subtitle>
            {t('pages.home.control_section.subtitle_line1')}
          </Subtitle>
          <ButtonContainer>
            <div className="d-flex align-items-center gap-2 mt-3">
              <img src={IOSDownloadIcon} alt="Managd Logo" width={126} style={{ cursor: "pointer" }} onClick={() => {
                openExternalLink("https://apps.apple.com/de/app/managd/id6745959836");
              }} />
              <img src={AndroidDownloadIcon} alt="Managd Logo" width={130} style={{ cursor: "pointer" }} onClick={() => {
                openExternalLink("https://play.google.com/store/apps/details?id=com.getmanagd.app");
              }} />
            </div>
          </ButtonContainer>
        </StageFooter>
      </Stage>
    </Section>
  );
};

export default ControlAnywhere;
